/**
 * One turn of the transcript, with the reconcile passes that committed it
 * folded underneath.
 *
 * Three things this component refuses to hide:
 *
 *   - `possible_duplicate`. PD-1 sets it on BOTH candidates when a legacy turn
 *     that both Postgres stores hold could not be bridged — which of the two is
 *     the extra copy is exactly what is unknown. Drawing either as a plain turn
 *     would state a fact the payload explicitly declines to state.
 *   - `plane`. A pebble that flipped mid-conversation is one transcript with two
 *     regimes in it, and which turn belongs to which is the point of the screen.
 *   - `origins` and `ts_source`. Every line traces back to the store and the
 *     column it came from, so an operator can go read the row.
 */
import { useState } from 'react';
import { Badge, RawJson, type Tone } from './ui';
import VerdictCard from './VerdictCard';
import { formatClock, type TurnRow as TurnRowModel } from './trace';

const ROLE_TONE: Record<string, Tone> = { user: 'blue', ai: 'green', system: 'gray' };

const ORIGIN_LABEL: Record<string, string> = {
  transcript_archive: 'archive',
  rtdb: 'rtdb',
  pebble_chats: 'pebble_chats',
};

export default function TurnRow({ row, defaultOpen = false }: { row: TurnRowModel; defaultOpen?: boolean }) {
  const { entry, verdicts } = row;
  const [open, setOpen] = useState(defaultOpen);
  // `expand passes` is a LIVE control, not a mount-time default. Seeding
  // `useState` from it and stopping there made the checkbox a dead control: the
  // row is keyed by its own identity, so nothing remounts when the flag flips,
  // and ticking the box rewrote the URL while the screen stayed collapsed.
  // React's documented "adjusting state when a prop changes" — re-render during
  // render, no effect, no flash of the stale value.
  const [flag, setFlag] = useState(defaultOpen);
  if (flag !== defaultOpen) {
    setFlag(defaultOpen);
    setOpen(defaultOpen);
  }
  const role = entry?.role ?? 'system';
  const isUser = role === 'user';
  const hasDetail = verdicts.length > 0;

  return (
    <div className={`flex ${isUser ? 'justify-start' : 'justify-end'}`} data-testid="turn-row">
      <div className={`w-full md:w-11/12 ${isUser ? '' : 'md:ml-auto'}`}>
        <div
          className={`rounded-lg border p-3 ${
            entry?.possible_duplicate
              ? 'border-amber-300 bg-amber-50'
              : isUser
                ? 'border-gray-200 bg-white'
                : 'border-green-200 bg-green-50/40'
          }`}
        >
          {/* Provenance strip */}
          <div className="flex items-center gap-2 flex-wrap text-xs mb-2">
            <Badge tone={ROLE_TONE[role] ?? 'gray'}>{role}</Badge>
            <Badge tone={entry?.plane === 'split' ? 'purple' : 'gray'}>{entry?.plane ?? 'unknown plane'}</Badge>
            <span className="text-gray-500">#{entry?.seq}</span>
            <span className="text-gray-500" title={entry?.ts_source ?? 'no timestamp source'}>
              {formatClock(entry?.ts ?? null)}
              {entry?.ts_source ? ` (${entry.ts_source})` : ''}
            </span>
            {(entry?.origins ?? []).map((o, i) => (
              <Badge key={`${i}-${o}`} tone="gray" title={o}>
                {ORIGIN_LABEL[o] ?? o}
              </Badge>
            ))}
            {entry?.archive_source && <Badge tone="gray">src: {entry.archive_source}</Badge>}
            {entry?.possible_duplicate && (
              <Badge tone="amber" title="This line may be the other half of another line — PD-1 could not bridge them, and does not claim which is the copy.">
                possible duplicate
              </Badge>
            )}
          </div>

          <div className="whitespace-pre-wrap text-sm text-gray-900">{entry?.content}</div>

          <div className="mt-2 flex items-center gap-3 flex-wrap text-xs text-gray-500">
            <code className="break-all">{entry?.message_key ?? entry?.pg_id ?? 'no key'}</code>
            {hasDetail && (
              <button type="button" className="text-pebbl-600 hover:text-pebbl-800" onClick={() => setOpen((v) => !v)}>
                {open ? '− hide' : '+ show'} {verdicts.length} reconcile pass{verdicts.length === 1 ? '' : 'es'}
              </button>
            )}
            {!hasDetail && entry?.plane === 'split' && role === 'ai' && (
              <span title="No verdict_archive row carries this message key — the pass may predate archiving, or the archive window may have dropped it.">
                no pass archived
              </span>
            )}
          </div>

          {entry?.attachments != null && <RawJson value={entry.attachments} label="attachments" />}
        </div>

        {open && (
          <div className="mt-2 space-y-2 pl-3 border-l-2 border-pebbl-200">
            {verdicts.map((v) => (
              <VerdictCard key={v.index} verdict={v} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
